import * as React from 'react';
import { useDispatch } from 'react-redux';
import * as _ from 'lodash';
import {
  API,
  SettingsContextObject,
  Logging,
  Notification,
  NotificationData,
  ResponseError,
  Setting,
  SettingsContext,
  UserInfo,
  PermissionGroup,
  UserContext,
  getEditPermission,
  settingCertificateCustomField,
} from '@vcd-ecm-ui/Common';
import Field from '../../Common/Field/Field';
import Footer from '../../Common/Footer/Footer';
import { FieldItem } from '../../Common/Field/FieldItem';
import CertificateCustomFieldsProps from './CertificateCustomFieldsProps.interface';
import { updateSettingsObject } from '../../Common/Common';
import '../../Common/common-settings.scss';
import '../certificate-settings.scss';

const originalValuePrefix = 'Custom Field';
const fieldLabel = 'Field Label';

const CertificateCustomFields = ({
  gatewayEndPoint,
  settings,
}: CertificateCustomFieldsProps): JSX.Element => {
  const [notification, setNotification] = React.useState(null);
  const { settingsContext, setSettingsContext } = React.useContext(SettingsContextObject);
  // Get the User Context
  const userContext: UserInfo = React.useContext(UserContext);
  const permissionSettingsEdit: boolean = getEditPermission(userContext, PermissionGroup.Settings);

  const dispatch = useDispatch();

  const getCertificateCustomFields = (): Setting[] => {
    Logging.Debug('getCertificateCustomFields');

    // Filter the List of Settings
    return settings.filter((x: Setting) => x.name === settingCertificateCustomField);
  };

  // Get the Initial Fields
  const getInitialFields = (): FieldItem[] => {
    const fieldsArray: FieldItem[] = [];

    getCertificateCustomFields().forEach((setting: Setting) => {
      const originalValue = `${originalValuePrefix} ${setting.sequenceNumber}`;
      const value = setting.value ? setting.value.toString() : originalValue;

      fieldsArray.push({
        index: setting.sequenceNumber,
        active: setting.activeInd,
        initialActive: setting.activeInd,
        originalValue,
        value,
        initialValue: value,
      });
    });

    return fieldsArray;
  };

  const [fields, setFields] = React.useState(getInitialFields());
  const [initialFields, setInitialFields] = React.useState(getInitialFields());

  // Field Active has Changed
  const onChangeActive = (index: number, active: boolean): void => {
    const fieldsCopy = _.cloneDeep(fields);

    fieldsCopy[index - 1].active = active;

    setFields(fieldsCopy);
  };

  // Field Value has Changed
  const onChangeValue = (index: number, value: string): void => {
    const fieldsCopy = _.cloneDeep(fields);

    fieldsCopy[index - 1].value = value;

    setFields(fieldsCopy);
  };

  // Render the Custom Fields
  const renderCustomFields = (): JSX.Element[] => {
    Logging.Debug('renderCustomFields', fields);

    // eslint-disable-next-line arrow-body-style
    return fields.map((item: FieldItem) => {
      return (
        <Field
          key={item.index}
          index={item.index}
          active={item.active}
          disabled={!permissionSettingsEdit}
          onChangeActive={onChangeActive}
          label={fieldLabel}
          originalValue={item.originalValue}
          value={item.value}
          onChangeValue={onChangeValue}
        />
      );
    });
  };

  // Get the Updated Settings
  const getUpdatedSettings = (): Setting[] => {
    const updatedSettings: Setting[] = [];

    fields.forEach((field: FieldItem) => {
      updatedSettings.push({
        name: settingCertificateCustomField,
        sequenceNumber: field.index,
        activeInd: field.active,
        value: field.value,
        dataType: 'STRING',
      });
    });

    return updatedSettings;
  };

  // Get the Updated Fields
  const getUpdatedFields = (): FieldItem[] => {
    const updatedFields: FieldItem[] = [];

    fields.forEach((field: FieldItem) => {
      updatedFields.push({
        index: field.index,
        active: field.active, // Set the Initial Active to the Current Active
        initialActive: field.active,
        originalValue: field.originalValue,
        value: field.value,
        initialValue: field.value, // Set the Initial Value to the Current Value
      });
    });

    return updatedFields;
  };

  // Make the API Call to Save the Certificate Custom Fields
  const saveCertificateCustomFields = () => async (): Promise<void> => {
    try {
      const api = new API(gatewayEndPoint);
      const updatedSettings: Setting[] = getUpdatedSettings();

      // Save the Settings
      await api.settings.setCertificateSettingsV4(updatedSettings);

      // Update the Settings Object
      const settingsObject: SettingsContext = updateSettingsObject(settingsContext, 'certificates', updatedSettings);

      // Update the Certicate Settings Context
      setSettingsContext(settingsObject);

      // Update the Fields with the saved Changes
      const updatedFields: FieldItem[] = getUpdatedFields();
      setFields(updatedFields);
      setInitialFields(updatedFields);

      const notificationData: NotificationData = {
        type: 'Information',
        title: 'Certificate Custom Fields Saved',
        description: 'The Certificate Custom Fields were saved.',
        autoClose: true,
        autoCloseTimeOutMilliseconds: 1000,
        position: 'BottomRight',
      };

      setNotification(notificationData);
    } catch (error) {
      const notificationData: NotificationData = {
        type: 'Error',
        title: 'Certificate Custom Fields Save Error',
        description: 'Unable to Save Certificate Custom Fields.',
        error: error as ResponseError,
      };

      setNotification(notificationData);
    }
  };

  // Save
  const onSave = (): void => {
    // Save
    Logging.Info('Save');

    dispatch(saveCertificateCustomFields());
  };

  // Cancel
  const onCancel = (): void => {
    // Save
    Logging.Info('Cancel');

    // Reset the Fields
    setFields(initialFields);
  };

  // Close the Notification
  const onNotificationClose = (): void => {
    setNotification(null);
  };

  // Check if the Values has Changed
  const valuesChanged = (): boolean => {
    let equal = true;

    fields.forEach((field: FieldItem) => {
      equal = equal && (field.active === field.initialActive) && (field.value === field.initialValue);
    });

    return !equal;
  };

  // Validate the Values
  const validateValues = (): boolean => {
    let validValues = true;

    fields.forEach((field: FieldItem) => {
      if (field.active) {
        const validValue = ((field.value !== null) && (field.value !== ''));
        validValues = validValues && validValue;
      }
    });

    return validValues;
  };

  // eslint-disable-next-line arrow-body-style
  React.useEffect(() => {
    // This Code will run once after the initial Render

    return (): void => {
      // This Code will run once the component unmounts.
      setNotification(null);
    };
  }, []);

  const cancelVisible = true;
  const cancelEnabled = valuesChanged();
  const saveVisible = true;
  const saveEnabled = validateValues() && valuesChanged();

  Logging.Info('Certificate Settings: Certificate Custom Fields');

  return (
    <>
      <div className="certificateCustomFieldsTab">
        {renderCustomFields()}
      </div>
      <Footer
        onCancel={onCancel}
        cancelVisible={cancelVisible && permissionSettingsEdit}
        cancelEnabled={cancelEnabled}
        saveVisible={saveVisible && permissionSettingsEdit}
        saveEnabled={saveEnabled}
        onSave={onSave}
      />
      <Notification
        data={notification}
        onNotificationClose={onNotificationClose}
        autoClose
      />
    </>
  );
};

export default CertificateCustomFields;
